import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { selfRegisterOrganization } from "../../setup/api/services/organization";
import TextInput from "../common/hookForm/TextInput";
import TextArea from "../common/hookForm/TextArea";
import PrimaryButton from "../common/PrimaryButton";

// TextInput requires onBlur; these fields have no blur behaviour.
const noop = () => undefined;

/** What the gate needs to render its shell for a just-submitted org, before any refetch. */
export interface ISubmittedOrganization {
  /** Returned by the backend (it generates the code); required to attach onboarding documents. */
  code: string;
  name: string;
  logoUrl?: string;
}

interface Props {
  onSubmitted: (organization: ISubmittedOrganization) => void;
  /** Pre-fills the form for a resubmission after rejection. */
  initialValues?: Partial<IFormValues>;
}

// Same three fields as the Waffy ID self-registration page. The organization code is generated
// by the backend and the logo is uploaded later from the sidebar.
interface IFormValues {
  name: string;
  websiteUrl: string;
  description: string;
}

const EMPTY_VALUES: IFormValues = {
  name: "",
  websiteUrl: "",
  description: "",
};

const WEBSITE_URL_PATTERN = /^https?:\/\/[^\s]+\.[^\s]+$/i;

// Known backend business-rule messages (see OrganizationServiceImpl.selfRegister /
// resubmitRejectedOrganization in authentication-service), mapped to translation keys.
const KNOWN_ERROR_KEYS: Record<string, string> = {
  "code already exists": "lbl_org_error_code_exists",
  "You already belong to an organization": "lbl_org_error_already_member",
  "Organization code cannot be changed on resubmission":
    "lbl_org_error_code_immutable",
};

/**
 * The most specific message a failed self-registration carries, in order: field validation
 * errors (HTTP 400 `validationErrors`, Arabic first), a known business rule, the backend's own
 * message, a network failure (fetch rejects with a TypeError), then the generic fallback.
 */
const getSubmitErrorMessage = (
  err: any,
  t: (key: string) => string
): string => {
  const validationErrors = err?.error?.validationErrors;
  if (validationErrors && typeof validationErrors === "object") {
    const messages = Object.values(validationErrors)
      .flat()
      .map((e: any) =>
        typeof e === "string" ? e : e?.messageAr || e?.message || ""
      )
      .filter(Boolean);
    if (messages.length) return messages.join("، ");
  }
  const backendMessage: string | undefined = err?.error?.message;
  if (backendMessage && KNOWN_ERROR_KEYS[backendMessage]) {
    return t(KNOWN_ERROR_KEYS[backendMessage]);
  }
  if (err?.error?.messageAr) return err.error.messageAr;
  if (backendMessage) return backendMessage;
  if (err instanceof TypeError) return t("lbl_org_error_network");
  return t("lbl_org_submit_error");
};

const CreateOrganizationForm: React.FC<Props> = ({
  onSubmitted,
  initialValues,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<IFormValues>({
    name: initialValues?.name ?? EMPTY_VALUES.name,
    websiteUrl: initialValues?.websiteUrl ?? EMPTY_VALUES.websiteUrl,
    description: initialValues?.description ?? EMPTY_VALUES.description,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField =
    (field: keyof IFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  // The website is optional, but when it is filled in it must be a valid address.
  const isWebsiteValid = useMemo(
    () =>
      values.websiteUrl.trim() === "" ||
      WEBSITE_URL_PATTERN.test(values.websiteUrl.trim()),
    [values.websiteUrl]
  );

  const canSubmit = values.name.trim() !== "" && isWebsiteValid;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!canSubmit) {
      setError(
        !isWebsiteValid
          ? t("lbl_org_field_website_invalid")
          : t("lbl_org_required_field")
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const name = values.name.trim();
      const response = await selfRegisterOrganization({
        name,
        websiteUrl: values.websiteUrl.trim() || undefined,
        description: values.description.trim() || undefined,
      });
      // The backend generates the code (or keeps the existing one on a resubmission).
      onSubmitted({
        code: response?.data?.code ?? "",
        name,
        logoUrl: response?.data?.logoUrl || undefined,
      });
    } catch (err: any) {
      setError(getSubmitErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div>
        <h2 className={styles.title}>{t("lbl_org_onboarding_title")}</h2>
        <p className={styles.subtitle}>{t("lbl_org_onboarding_subtitle")}</p>
      </div>
      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.field}>
          <TextInput
            name="name"
            value={values.name}
            onChange={setField("name")}
            onBlur={noop}
            placeholder={`${t("lbl_org_field_name")} *`}
          />
        </div>
        <div className={styles.field}>
          <TextInput
            name="websiteUrl"
            type="url"
            dir="ltr"
            value={values.websiteUrl}
            onChange={setField("websiteUrl")}
            onBlur={noop}
            placeholder={t("lbl_org_field_website_optional") as string}
            hint="https://example.com"
          />
        </div>
        <div className={styles.field}>
          <TextArea
            name="description"
            value={values.description}
            onChange={setField("description")}
            label={t("lbl_org_field_description") as string}
            showLabel
            showMaxLength={false}
          />
        </div>
        {error && (
          <p className={styles.error} role="alert" dir="auto">
            {error}
          </p>
        )}
        <div className={styles.submitRow}>
          {/* Kept enabled-looking while submitting so the spinner stays visible; onSubmit
              ignores repeat clicks. */}
          <PrimaryButton
            type="submit"
            disabled={!canSubmit}
            aria-busy={submitting}>
            {submitting ? (
              <span className={styles.submitLoading}>
                <span className={styles.spinner} />
                {t("lbl_org_submitting")}
              </span>
            ) : (
              t("lbl_org_submit")
            )}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default CreateOrganizationForm;
